import React, { useCallback, useContext } from 'react';
import { StyleSheet, View } from 'react-native';
import FastImage from 'utils/packages/rnFastImage';
import { CardView } from 'design-system/CardView';
import { rem } from 'design-system/values';
import { Separator } from 'design-system/Separator';
import { SettingsItemIcon } from 'design-system/SettingsItemIcon';
import Spacer from 'design-system/Spacer';
import Amount from 'design-system/Amount';
import Button from 'design-system/Button';
import Text from 'design-system/Text';
import { formatDateForItem } from 'utils';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { getNotTrackingButtonState, getNotTrackingInfo, RENT_REPORTING_AGENCIES, } from 'features/rent-reporting/utils';
import useNotTrackingCTAPress from 'features/rent-reporting/hooks/useNotTrackingCTAPress';
import { UnavailableOnWebModal } from 'utils/packages/UnavailableOnWebModal';
import IconHomeBold from 'design-system/icons/IconHomeBold';
import RentReportingStatus from '../../../rent-reporting/components/RentReportingStatus';
import { isWeb } from '../../../../constants';
import { WalkthroughContext } from '../walkthrough/context';
const RentReportingActiveCard = ({ data }) => {
    const navigation = useMainStackNavigation();
    const isTracking = data.isTracking === true || data.isTracking === undefined;
    const isUnlockedForTier = data.reportingAgencies != null && data.reportingAgencies.length > 0;
    const hasIssue = !isTracking || !isUnlockedForTier;
    const onPressManage = useCallback(() => {
        if (isWeb) {
            UnavailableOnWebModal.show('emma://rent-reporting', 'Manage rent reporting');
        }
        else {
            navigation.navigateDeprecated('RentReporting');
        }
    }, [navigation]);
    const onPressCTA = useNotTrackingCTAPress();
    const { ctaInfo } = getNotTrackingButtonState(data);
    // hasIssue also covers issues without a not-tracking error (tier lock,
    // missing reason) — those still error-style the manage button
    const manageButtonHasError = hasIssue && !ctaInfo;
    const onPressNotTrackingCTA = useCallback(() => {
        if (ctaInfo) {
            onPressCTA(data);
        }
    }, [ctaInfo, data, onPressCTA]);
    const isInWalkthrough = useContext(WalkthroughContext);
    return (<CardView noMarginTop noMarginBottom noMargin={isInWalkthrough}>
      <Spacer h={16}/>
      <View style={styles.headerContainer}>
        <View style={styles.headerRow}>
          <SettingsItemIcon iconSize={44}>
            <IconHomeBold gradientKey="peach"/>
          </SettingsItemIcon>
          <Spacer w={16}/>
          <View style={styles.flex}>
            <RentReportingStatus active={!!(isTracking && isUnlockedForTier)}/>
            <Text Text-16>Rent reporting</Text>
          </View>
        </View>
      </View>
      <Spacer h={16}/>
      <Separator />
      {isUnlockedForTier ? (<View style={styles.rowsContainer}>
          <View style={styles.row}>
            <Text TextThin-14 Secondary flex>
              Next payment
            </Text>
            <Text Text-14>{formatDateForItem(data.nextPaymentDate)}</Text>
          </View>
          <View style={styles.row}>
            <Text TextThin-14 Secondary flex>
              Amount
            </Text>
            <Amount Numbers-14 value={{ amount: data.amount, currency: data.currency }}/>
          </View>
          <View style={styles.row}>
            <Text TextThin-14 Secondary flex>
              Reporting to
            </Text>
            <View style={styles.statusRow}>
              {data.reportingAgencies?.map((item) => RENT_REPORTING_AGENCIES[item] ? (<FastImage key={item} source={RENT_REPORTING_AGENCIES[item].image} accessibilityLabel={RENT_REPORTING_AGENCIES[item].label} style={styles.providerLogo}/>) : null)}
            </View>
          </View>
        </View>) : null}
      {hasIssue ? (<Text TextThin-14 Negative style={styles.notTrackingReason} testID="rentReportingNotTrackingReason">
          {getNotTrackingInfo(data).description}
        </Text>) : null}
      {ctaInfo ? (<Button errorReversed square title={ctaInfo.cta} style={styles.ctaButton} onPress={onPressNotTrackingCTA}/>) : null}
      <Button square {...(manageButtonHasError
        ? { errorReversed: true }
        : { brandReversed: true })} style={[styles.button, !!ctaInfo && styles.buttonBelowCTA]} onPress={onPressManage} title={manageButtonHasError ? 'Action required' : 'Manage reporting'}/>
    </CardView>);
};
const styles = StyleSheet.create({
    headerContainer: {
        paddingHorizontal: rem(16),
    },
    headerRow: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    rowsContainer: {
        paddingVertical: rem(4),
    },
    row: {
        paddingHorizontal: rem(16),
        paddingVertical: rem(12),
        flexDirection: 'row',
        alignItems: 'center',
    },
    statusRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    button: {
        margin: rem(16),
        marginTop: rem(8),
    },
    ctaButton: {
        marginHorizontal: rem(16),
        marginTop: rem(8),
    },
    buttonBelowCTA: {
        marginTop: rem(12),
    },
    notTrackingReason: {
        paddingHorizontal: rem(16),
        paddingBottom: rem(8),
        paddingTop: rem(8),
    },
    providerLogo: {
        width: rem(32),
        height: rem(32),
        marginLeft: rem(8),
    },
});
export default RentReportingActiveCard;
